'use client';

import { usePulseTracker } from '@cineamo/pulse/src/react/usePulseTracker';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { SeatType } from '@cineamo/api/models/seat/SeatDto.types';
import { SeatCategoryDto } from '@cineamo/api/models/seat-category/SeatCategoryDto.types';
import { WebsiteInputStyle, WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

import { toBackendLocale } from '@cineamo/ui/src/constants/locale-constants';

import { apiResponseHandler } from '@cineamo/api/helpers/api-response-helper/api-response-helper';
import { setCartConcessions } from '@cineamo/api/resources/cart-concession/cart-concession-api';
import { PaymentApiSessionResponse } from '@cineamo/api/resources/payment-api/CineamoPaymentApiResponse.types';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { getLocaleFromLanguage } from '@cineamo/ui/src/helpers/locale-helper';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { useConcessionCatalog } from '@cineamo/composables/src/hooks/useConcessionCatalog';
import { useMovie } from '@cineamo/composables/src/hooks/useMovie';
import useSessionStorage from '@cineamo/composables/src/hooks/useSessionStorage';
import useStripeUrlQueryParams from '@cineamo/composables/src/hooks/useStripeUrlQueryParams';

import { IconBack16 } from '@cineamo/ui/src/icons/ic-back';
import { IconLogout16 } from '@cineamo/ui/src/icons/ic-logout';
import { IconPlus16 } from '@cineamo/ui/src/icons/ic-plus';
import { screenFilmFromMovie } from '@cineamo/ui/src/room-3d/screen-film-from-movie';
import WebsiteHeader from '@cineamo/ui/src/website/WebsiteHeader';

import CenterLayout from '@cineamo/composables/src/CenterLayout';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import { BookingStep, useCartProvider } from '@cineamo/composables/src/providers/cartProvider';
import useUserStore from '@cineamo/composables/src/stores/useUserStore';
import CheckoutButton from '@cineamo/composables/src/ticket-booking/CheckoutButton';
import Payment from '@cineamo/composables/src/ticket-booking/Payment';
import { CheckoutShowingInfoContext } from '@cineamo/composables/src/ticket-booking/showing-info-context';
import ShowingDetails from '@cineamo/composables/src/ticket-booking/ShowingDetails';

import { useSeatMap } from '../hooks/useSeatMap';

import { BuyerDetails, createPaymentIntent, syncSeatLocks } from './actions';
import BookingFooter from './BookingFooter';
import CartSummary from './CartSummary';
import { isConcessionsWithoutTicketsError } from './concession-helper';
import LockCountdown from './countdown/LockCountdown';
import EmailInputModal from './EmailInputModal';
import GiftCardRedemption from './GiftCardRedemption';
import PaymentFailedView from './PaymentFailedView';
import PaymentSucceededView from './PaymentSucceededView';
import { legendFurnitureOf } from './SeatMapLegend';
import SeatSelection from './SeatSelection';
import SnackSelectionModal from './SnackSelectionModal';
import { useErrorHandler } from './useErrorHandler';

const TicketBookingPage: React.FC<{
    seatCategories: SeatCategoryDto[];
    showCinemaName?: boolean;
    websiteStyle: WebsiteStyle & WebsiteInputStyle;
    sidebarStyle: string;
    contentMaxHeight: string;
    /** The page's header has a back arrow on small screens, so the seat picking step needs no link of its own. */
    headerHasBackButton?: boolean;
    /**
     * The app's showing info, opened by "Details" on small screens; it reads its open state with
     * `useCheckoutShowingInfo`. Left out, "Details" links to the film's page.
     */
    showingInfoModal?: React.ReactNode;
    /** The cinema sells concessions online (`cinemaConfiguration.isConcessionSaleEnabled`). */
    isConcessionSaleEnabled?: boolean;
    /** Shows the gift card field in the cart step; off by default, as the cinema has to have redemption enabled. */
    isGiftCardRedemptionEnabled?: boolean;
}> = ({
    seatCategories,
    showCinemaName,
    websiteStyle,
    sidebarStyle,
    contentMaxHeight,
    headerHasBackButton,
    showingInfoModal,
    isConcessionSaleEnabled = false,
    isGiftCardRedemptionEnabled
}) => {
    const {
        selectedSeats,
        setItems,
        currency,
        cartUuid,
        paidCartUuid,
        setPaidCartUuid,
        showing,
        items,
        isProcessing,
        total,
        concessions,
        setConcessions,
        refreshCartDto,
        currentStep,
        setCurrentStep
    } = useCartProvider();
    const { handleErrorResponse } = useErrorHandler();
    const { user } = useUserStore();
    const locale = useLocale();
    const [isPageHaveUnsavedChanges, setIsPageHaveUnsavedChanges] = useSessionStorage(
        'isPageHaveUnsavedChanges',
        'false'
    );

    const { seats, room, roomObjects, isLoading: isSeatMapLoading } = useSeatMap(showing?.cinemaRoomId);
    const { movie } = useMovie(showing?._embedded?.content?.movieId, locale, showing?.cinemaId);
    const screenFilm = useMemo(() => (movie ? (screenFilmFromMovie(movie) ?? undefined) : undefined), [movie]);
    const { track } = useAnalytics();
    const { track: trackPulse } = usePulseTracker();

    const seatsInCart = useMemo(() => items.filter((item) => item._embedded?.product?.type === 'SeatLock'), [items]);
    const { paymentIntentClientSecret, redirectStatus } = useStripeUrlQueryParams();

    const t = useTranslations('tickets');
    const { tabs: concessionTabs } = useConcessionCatalog(showing?.cinemaId ?? 0, isConcessionSaleEnabled);

    useEffect(() => {
        // Snacks are only sold together with tickets (`concessions_without_tickets`).
        if (selectedSeats.length > 0 || concessions.length === 0 || !cartUuid) return;
        void setCartConcessions(
            cartUuid,
            concessions.flatMap(({ cinemaProductId }) =>
                cinemaProductId === null ? [] : [{ cinemaProductId, quantity: 0 }]
            )
        ).then(() => refreshCartDto());
        setConcessions([]);
    }, [selectedSeats.length, concessions, cartUuid, setConcessions, refreshCartDto]);

    const [snackSelectionModalIsOpen, setSnackSelectionModalIsOpen] = useState(false);
    const [isShowingInfoOpen, setIsShowingInfoOpen] = useState(false);
    const showingInfoState = useMemo(
        () => ({ isOpen: isShowingInfoOpen, close: () => setIsShowingInfoOpen(false) }),
        [isShowingInfoOpen]
    );
    const [emailModalIsOpen, setEmailModalIsOpen] = useState(false);
    const [isCheckoutButtonLoading, setIsCheckoutButtonLoading] = useState(false);
    const [isEmailModalLoading, setIsEmailModalLoading] = useState(false);
    const [emailError, setEmailError] = useState<string>();
    const [paymentSubmitTrigger, setPaymentSubmitTrigger] = useState(0);
    const [stripeClientSecret, setStripeClientSecret] = useState<string | null | null>(null);
    // The modal unmounts on close, so a guest's entry would be gone when they return from the payment step.
    const [lastBuyer, setLastBuyer] = useState<BuyerDetails>();

    const handleCreatePaymentIntent = useCallback(
        async (buyer: BuyerDetails) => {
            setLastBuyer(buyer);
            setIsEmailModalLoading(true);
            try {
                const res = await createPaymentIntent(
                    cartUuid,
                    buyer,
                    currency,
                    // An /en-US/ page would otherwise send en_US, which the API rejects (CIN-15225).
                    toBackendLocale(getLocaleFromLanguage(locale))
                );
                apiResponseHandler<PaymentApiSessionResponse>(
                    res,
                    async (successResponse) => {
                        setEmailModalIsOpen(false);
                        // Nothing is due (a free showing, or gift cards cover everything): the backend has
                        // already completed the order, so there is no payment step (CIN-15034).
                        if (!('client_secret' in successResponse)) {
                            setPaidCartUuid(cartUuid);
                            setCurrentStep('03_payment_complete');
                            return;
                        }
                        setStripeClientSecret(successResponse.client_secret);
                        setCurrentStep('02_payment');
                    },
                    (errorResponse) => {
                        // The address itself was refused, e.g. a domain that cannot receive mail: say so
                        // beside the field, where the buyer can fix it (CIN-15349).
                        if ('receiptEmail' in (errorResponse.data?.parameters ?? {})) {
                            setEmailError(t('tickets.payment.email-input.email-undeliverable'));
                            return;
                        }
                        showToast({
                            toastType: toast.info,
                            // `detail` carries the reason ("Bitte wählen Sie die Plätze erneut aus. (C5200)");
                            // `title` is the generic problem type ("Invalid parameter") and says nothing.
                            messageTitle: isConcessionsWithoutTicketsError(errorResponse)
                                ? t('error.concessions-without-tickets')
                                : errorResponse.data?.detail ||
                                  errorResponse.data?.title ||
                                  t('error.something-went-wrong')
                        });
                    }
                );
            } catch {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('error.something-went-wrong')
                });
            } finally {
                setIsEmailModalLoading(false);
            }
        },
        [cartUuid, currency, locale, setCurrentStep, setPaidCartUuid, t]
    );

    const onCheckoutButtonClick = useCallback(async () => {
        switch (currentStep) {
            case '01_seat_picking':
                {
                    setIsCheckoutButtonLoading(true);
                    const result = await syncSeatLocks(cartUuid, showing.id, selectedSeats, items);
                    apiResponseHandler(
                        result?.response ?? null,
                        async () => {
                            const cartItems = result.cartItems;
                            setItems(cartItems);
                            // The totals of step 01.5 and the payment are the API cart's, so read them once synced.
                            await refreshCartDto();
                            setCurrentStep('01.5_cart');
                        },
                        (errorResponse) => {
                            handleErrorResponse(errorResponse);
                        }
                    );
                    setIsCheckoutButtonLoading(false);
                }
                break;
            case '01.5_cart':
                {
                    // CIN-15320: a name is obligatory, so the account only skips the modal when it
                    // actually carries both parts. An account missing a name goes through the modal
                    // like a guest, prefilled with whatever it does have.
                    if (user?.email && user?.firstName && user?.lastName) {
                        handleCreatePaymentIntent({
                            email: user.email,
                            firstName: user.firstName,
                            lastName: user.lastName
                        });
                    } else {
                        setEmailModalIsOpen(true);
                    }
                }
                break;
            case '02_payment':
                setPaymentSubmitTrigger(paymentSubmitTrigger + 1);
                break;
        }
    }, [
        cartUuid,
        currentStep,
        handleErrorResponse,
        items,
        paymentSubmitTrigger,
        refreshCartDto,
        selectedSeats,
        setCurrentStep,
        setItems,
        showing.id,
        user?.email,
        user?.firstName,
        user?.lastName,
        handleCreatePaymentIntent,
        t
    ]);

    useLayoutEffect(() => {
        if (cartUuid === paidCartUuid) {
            setCurrentStep('03_payment_complete');
        }
    }, [cartUuid, paidCartUuid, setCurrentStep]);

    useEffect(() => {
        if (paymentIntentClientSecret) {
            setStripeClientSecret(paymentIntentClientSecret);
            setCurrentStep('03_payment_complete');
        }
    }, [paymentIntentClientSecret, setCurrentStep]);

    useEffect(() => {
        setCurrentStep('01_seat_picking');
    }, [selectedSeats.length, setCurrentStep]);

    const previousStep = useRef<BookingStep>(currentStep);
    useEffect(() => {
        if (previousStep.current !== currentStep) {
            track('Step Changed', {
                previousStep: previousStep.current,
                currentStep: currentStep,
                cartTotal: total.toString(),
                cartItemsCount: items.length
            });
            previousStep.current = currentStep;
        }

        // One value, set once: setting it twice in one run (seat picking with no seat chosen while the cart still
        // holds seat locks) flipped it between renders, and since the setter changes with the value the effect
        // looped. The rules are the old ones, the later one winning.
        let hasUnsavedChanges = currentStep === '01_seat_picking' ? selectedSeats.length > 0 : undefined;
        if (selectedSeats.length !== seatsInCart.length) hasUnsavedChanges = true;
        if (currentStep === '01.5_cart' || currentStep === '02_payment') hasUnsavedChanges = true;
        if (currentStep === '03_payment_complete' || currentStep === '03_payment_error') hasUnsavedChanges = false;
        if (hasUnsavedChanges !== undefined) {
            setIsPageHaveUnsavedChanges(hasUnsavedChanges ? 'true' : 'false');
        }
    }, [
        currentStep,
        items.length,
        seatsInCart.length,
        selectedSeats.length,
        setIsPageHaveUnsavedChanges,
        total,
        track
    ]);

    useEffect(() => {
        if (redirectStatus === 'succeeded') {
            setCurrentStep('03_payment_complete');
        }
        if (redirectStatus === 'failed') {
            setCurrentStep('03_payment_error');
        }
    }, [redirectStatus, setCurrentStep]);

    // Pulse ticket_purchase on confirmed payment only: the Stripe redirect back with
    // `redirect_status=succeeded`, or an in-session paid cart. Reaching the
    // 03_payment_complete step alone is not enough — it is also entered while the payment
    // intent is still open.
    const pulsePurchaseTrackedRef = useRef(false);
    useEffect(() => {
        const isConfirmedPurchase = redirectStatus === 'succeeded' || (!!cartUuid && cartUuid === paidCartUuid);
        if (!isConfirmedPurchase || pulsePurchaseTrackedRef.current || !showing?.movieId) return;
        pulsePurchaseTrackedRef.current = true;
        trackPulse({
            movie_id: showing.movieId,
            event_type: 'ticket_purchase',
            context: {
                showing_id: showing.id,
                cart_uuid: paidCartUuid || cartUuid
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [redirectStatus, cartUuid, paidCartUuid]);

    const showingDetails = useMemo(() => {
        return (
            <>
                <ShowingDetails
                    showing={showing}
                    movie={movie ?? undefined}
                    showCinemaName={showCinemaName}
                    cinemaRoom={room?.dto ?? undefined}
                />
                <LockCountdown />
            </>
        );
    }, [showing, movie, showCinemaName, room?.dto]);

    const onPageUnload = useCallback(
        (e: BeforeUnloadEvent) => {
            if (isPageHaveUnsavedChanges === 'true') {
                e.preventDefault();
            }
        },
        [isPageHaveUnsavedChanges]
    );

    useEffect(() => {
        if (isPageHaveUnsavedChanges) {
            window.addEventListener('beforeunload', onPageUnload);
        }
        if (!isPageHaveUnsavedChanges) {
            window.removeEventListener('beforeunload', onPageUnload);
        }
        return () => {
            window.removeEventListener('beforeunload', onPageUnload);
        };
    }, [onPageUnload, isPageHaveUnsavedChanges]);

    if (!showing) {
        return (
            <CenterLayout className="mt-32">
                <WebsiteHeader title="Showing not found!" />
            </CenterLayout>
        );
    }

    let currentStepView: React.JSX.Element;
    switch (currentStep) {
        case '01_seat_picking':
            currentStepView = (
                <SeatSelection
                    className="w-full"
                    isLoading={isSeatMapLoading}
                    room={room}
                    seats={seats}
                    roomObjects={roomObjects}
                    seatCategories={seatCategories}
                    selectedSeatColor={websiteStyle.primaryColor}
                    screenFilm={screenFilm}
                />
            );
            break;
        case '01.5_cart':
            currentStepView = (
                <SeatSelection
                    className="hidden lg:flex w-full"
                    room={room}
                    seats={seats}
                    roomObjects={roomObjects}
                    seatCategories={seatCategories}
                    selectedSeatColor={websiteStyle.primaryColor}
                    screenFilm={screenFilm}
                />
            );
            break;
        case '02_payment':
            currentStepView = (
                <Payment
                    clientSecret={stripeClientSecret ?? ''}
                    submitTrigger={paymentSubmitTrigger}
                    websiteStyle={websiteStyle}
                />
            );
            break;
        case '03_payment_complete':
            currentStepView = <PaymentSucceededView />;
            break;
        case '03_payment_error':
            currentStepView = <PaymentFailedView />;
            break;
    }

    let backButton: React.JSX.Element | undefined;
    switch (currentStep) {
        case '01_seat_picking':
            backButton = (
                <Link
                    href={`/program/${showing._embedded?.content?.id}`}
                    className="lg:px-12 lg:py-6 py-8 opacity-50 flex row items-center gap-8">
                    <IconBack16 /> {t('other-screenings')}
                </Link>
            );
            break;
        case '01.5_cart':
            backButton = (
                <Link
                    href=""
                    onClick={() => setCurrentStep('01_seat_picking')}
                    className="lg:px-12 lg:py-6 py-8 opacity-50 flex row items-center gap-8">
                    <IconBack16 /> {t('back-to-seat-selection')}
                </Link>
            );
            break;
        case '02_payment':
            backButton = (
                <Link
                    href=""
                    onClick={() => setCurrentStep('01.5_cart')}
                    className="lg:px-12 lg:py-6 py-8 opacity-50 flex row items-center gap-8">
                    <IconBack16 /> {t('back-to-shopping-cart')}
                </Link>
            );
            break;
        case '03_payment_complete':
            backButton = (
                <Link
                    href={'/'}
                    className="lg:px-12 lg:py-6 py-8 opacity-50 flex row items-center gap-8">
                    <IconLogout16 /> {t('to-landing-page')}
                </Link>
            );
            break;
        case '03_payment_error':
            break;
    }

    const isSidebarVisible = !['03_payment_complete', '03_payment_error'].includes(currentStep);
    const hasTables = roomObjects.some((roomObject) => roomObject.type === 'table');
    const hasWheelchairSeats = seats.some((seat) => [SeatType.WHEELCHAIR, SeatType.DISABLED].includes(seat.type));
    const furniture = legendFurnitureOf(seats);

    return (
        <div className="relative flex flex-col lg:grow items-stretch">
            <div className="flex flex-col lg:flex-row lg:grow items-stretch">
                {snackSelectionModalIsOpen && (
                    <SnackSelectionModal
                        modalIsOpen={snackSelectionModalIsOpen}
                        setModalIsOpen={(value) => setSnackSelectionModalIsOpen(value)}
                        onClose={() => setSnackSelectionModalIsOpen(false)}
                        tabs={concessionTabs}
                    />
                )}
                {showingInfoModal && (
                    <CheckoutShowingInfoContext.Provider value={showingInfoState}>
                        {showingInfoModal}
                    </CheckoutShowingInfoContext.Provider>
                )}
                {emailModalIsOpen && (
                    <EmailInputModal
                        modalIsOpen={emailModalIsOpen}
                        setModalIsOpen={setEmailModalIsOpen}
                        onClose={() => setEmailModalIsOpen(false)}
                        onSubmit={handleCreatePaymentIntent}
                        isLoading={isEmailModalLoading}
                        emailError={emailError}
                        onEmailChange={() => setEmailError(undefined)}
                        websiteStyle={websiteStyle}
                        defaultValues={
                            lastBuyer ?? {
                                email: user?.email,
                                firstName: user?.firstName,
                                lastName: user?.lastName
                            }
                        }
                    />
                )}
                <div
                    className={cn(
                        'grow flex flex-col space-y-[12px] overflow-y-auto lg:overflow-y-hidden text-[var(--on-background-color)]',
                        contentMaxHeight,
                        sidebarStyle
                    )}>
                    <div
                        className={cn(
                            'lg:py-4 ml-[9px] lg:ml-32 shrink-0',
                            headerHasBackButton && currentStep === '01_seat_picking' && 'hidden lg:block'
                        )}>
                        {backButton}
                    </div>
                    <div className="grow flex flex-col min-h-0">
                        {isSidebarVisible && (
                            <div className="lg:hidden flex flex-col">
                                <ShowingDetails
                                    showing={showing}
                                    movie={movie ?? undefined}
                                    compactDetailsHref={`/program/${showing._embedded?.content?.id}`}
                                    onShowCompactDetails={
                                        showingInfoModal ? () => setIsShowingInfoOpen(true) : undefined
                                    }
                                />
                                <div className="flex flex-col mx-[9px]">
                                    <LockCountdown />
                                </div>
                            </div>
                        )}
                        <div className="w-full mx-auto lg:overflow-x-hidden space-y-16 lg:relative lg:h-0 lg:grow lg:min-h-0">
                            {currentStepView}
                        </div>
                        <div className="grow lg:hidden"></div>
                        <BookingFooter
                            seatCategories={seatCategories}
                            hasTables={hasTables}
                            hasWheelchairSeats={hasWheelchairSeats}
                            furniture={furniture}
                        />
                    </div>
                </div>

                {isSidebarVisible && (
                    <div
                        className={cn(
                            'relative lg:p-0 flex flex-col lg:w-[390px] lg:shrink-0 overflow-y-scroll pb-[var(--booking-footer-height,169px)] lg:pb-0',
                            contentMaxHeight
                        )}>
                        <div className="hidden lg:flex flex-col">{showingDetails}</div>

                        {seats.length > 0 && (
                            // While seats are picked, small screens show the list in the checkout footer.
                            <CartSummary
                                seats={seats}
                                className={currentStep === '01_seat_picking' ? 'hidden lg:block' : undefined}
                            />
                        )}

                        {isGiftCardRedemptionEnabled && currentStep === '01.5_cart' && (
                            <GiftCardRedemption
                                className="mx-[9px] mt-16 lg:mx-16"
                                websiteStyle={websiteStyle}
                            />
                        )}

                        <div className="grow" />

                        {isConcessionSaleEnabled &&
                            concessionTabs.length > 0 &&
                            selectedSeats.length > 0 &&
                            currentStep === '01.5_cart' && (
                                <button
                                    type="button"
                                    onClick={() => setSnackSelectionModalIsOpen(true)}
                                    className={cn(
                                        'mx-[9px] lg:mx-16 my-16 flex min-h-48 items-center justify-center gap-8 rounded-4 border-2 px-16 text-button-16 outline-none',
                                        'border-[var(--primary-color)] text-[var(--primary-color)]',
                                        'focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] focus-visible:ring-offset-2'
                                    )}>
                                    <IconPlus16 />
                                    {concessions.length > 0 ? t('edit-snacks') : t('add-snacks')}
                                </button>
                            )}

                        <CheckoutButton
                            onCheckoutClick={onCheckoutButtonClick}
                            seats={seats}
                            seatCategories={seatCategories}
                            hasTables={hasTables}
                            hasWheelchairSeats={hasWheelchairSeats}
                            furniture={furniture}
                            isLoading={isCheckoutButtonLoading || isProcessing}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default TicketBookingPage;
